/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import React from 'react';
import { keyframes } from '@compiled/react';
import findLastIndex from 'lodash/findLastIndex';
import memoize from 'lodash/memoize';
import take from 'lodash/take';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
	MarkPointComponent,
	MarkLineComponent,
	TooltipComponent,
	GridComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIsReducedMotion } from '@atlaskit/motion';

import { colors } from '@atlaskit/theme';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CHART_SPACE_LEFT,
	CHART_SPACE_RIGHT,
} from '../../../common/ui/issues-per-deployment-chart/constants.tsx';
import circleSelectedImage from './assets/circle-selected.svg';
import circleImage from './assets/circle.svg';
import messages from './messages.tsx';
import { useTooltipFormatter } from './tooltip/index.tsx';
import type { Props } from './types.tsx';

echarts.use([
	LineChart,
	BarChart,
	MarkPointComponent,
	MarkLineComponent,
	TooltipComponent,
	GridComponent,
	CanvasRenderer,
]);
const Chart = ({ dataPoints, medianIssues, onEvents }: Props) => {
	const { formatMessage, formatDate } = useIntl();
	const tooltipFormatter = useTooltipFormatter();
	const prefersReducedMotion = useIsReducedMotion();
	const selectedIndex = dataPoints.findIndex((dataPoint) => dataPoint.customInfo.isSelectedData);
	const lastCompleteWeekIndexWithData = findLastIndex(
		dataPoints,
		(dataPoint) => dataPoint.value !== null,
		dataPoints.length - 2,
	);
	const selectedPoint = selectedIndex === -1 ? null : dataPoints[selectedIndex];
	const axisCommonStyle = {
		nameGap: gridSize * 4,
		nameLocation: 'middle',
		nameTextStyle: {
			color: colors.N400,
			// This cannot be replaced with a token as ECharts requires a numeric pixel value.
			// Background: https://atlassian.slack.com/archives/C071DCS8MDL/p1733289846954599
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: 12,
		},
	};

	const TOOLTIP_ANIMATION = keyframes({
		from: {
			transform: 'translateY(14px)',
			opacity: 0,
		},
		to: {
			transform: 'translateY(0px)',
			opacity: 1,
		},
	});
	const TOOLTIP_APPEAR_TOP = {
		backgroundColor: colors.N900,
		borderWidth: 0,
		transitionDuration: 0,
		extraCssText: `animation: ${TOOLTIP_ANIMATION} 0.15s ease-out`,
		position: memoize(
			(
				pos: [number, number],
				params: { componentSubType: string; name: string; dataIndex: number },
				dom: HTMLElement,
				rect: { x: number; y: number; width: number; height: number },
				size: { contentSize: [number, number] },
			) => ({
				top: rect.y - size.contentSize[1] - gridSize,
				left: rect.x + 10 - size.contentSize[0] / 2,
			}),
			(pos, params) =>
				// memoize based on the index
				// otherwise we see calculation on mouse move and the tooltip jitters
				`${params.componentSubType}-${params.name}-${params.dataIndex}`,
		),
	} as const;

	// RLSS-2812 Support echarts with darkmode
	const chartOptions = {
		animation: !prefersReducedMotion,
		tooltip: {
			...TOOLTIP_APPEAR_TOP,
			formatter: tooltipFormatter,
		},
		grid: {
			left: CHART_SPACE_LEFT,
			right: CHART_SPACE_RIGHT,
			top: gridSize * 2,
			bottom: gridSize * 3,
		},
		xAxis: {
			data: dataPoints.map((data) => {
				const { startDateInclusive } = data.customInfo;

				return formatDate(startDateInclusive, {
					month: 'short',
					day: 'numeric',
				});
			}),
			axisLine: {
				lineStyle: {
					color: colors.N40,
					width: 1,
				},
			},
			axisTick: {
				alignWithLabel: true,
			},
			...axisCommonStyle,
		},
		yAxis: {
			name: fg('jira-issue-terminology-refresh-m3')
				? formatMessage(messages.yAxisLabelIssueTermRefresh)
				: formatMessage(messages.yAxisLabel),
			...axisCommonStyle,
			axisLine: {
				show: false,
			},
			splitNumber: 2,
			axisTick: false,
			splitLine: {
				lineStyle: {
					color: colors.N30,
				},
			},
		},
		textStyle: {
			color: colors.N400,
		},
		series: [
			{
				type: 'line',
				connectNulls: true,
				data: take(dataPoints, dataPoints.length - 1),
				color: colors.B100,
				lineStyle: {
					width: 1,
				},
				symbol: `image://${circleImage}`,
				symbolSize: 9,
				...(selectedPoint && selectedPoint.value !== null
					? {
							markPoint: {
								symbol: `image://${circleSelectedImage}`,
								silent: true,
								symbolSize: 22,
								data: [
									{
										coord: [selectedIndex, `${selectedPoint.value}`],
									},
								],
							},
						}
					: {}),
				...(medianIssues !== null
					? {
							markLine: {
								data: [
									{
										yAxis: medianIssues,
									},
								],
								silent: true,
								lineStyle: {
									color: colors.N300,
									width: 1,
								},
								symbol: 'none',
								label: { show: false },
							},
						}
					: {}),
			},
			{
				type: 'line',
				connectNulls: true,
				data: dataPoints.map((dataPoint, i) =>
					i < lastCompleteWeekIndexWithData ? null : dataPoint,
				),
				color: colors.B100,
				symbol: `image://${circleImage}`,
				symbolSize: 9,
				lineStyle: {
					width: 1,
					type: 'dashed',
					color: colors.B100,
				},
			},
		],
	};

	return (
		<ReactEchartsCore
			echarts={echarts}
			option={chartOptions}
			onEvents={onEvents}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ height: '100%' }}
		/>
	);
};

export default Chart;
